// The Network card on a deployment's Overview (spec 11 6.15.15): the network, the mode and per (provider, region) the
// block, the size and the attachment's state in words with its action; the standalone line; the off sentence.
import { EntryRoutes } from '../entryPoints/EntryRoutes';
import { NetworkCardRequests } from '../requests/DeploymentRequests';
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, Clock, Network as NetworkIcon, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Versioned } from '../../api/client';
import { useApi } from '../../api/context';
import { useNetwork } from '../../api/queries';
import type { Deployment, DeploymentNetwork, Run } from '../../api/types';
import { usePermission } from '../../auth/context';
import { useToast } from '../../components/toast';
import { ActionButton, StatusBadge } from '../../components/ui';
import { formatDateTime, formatRelative } from '../../lib/format';
import { strings } from '../../strings';
import { WaitingText } from '../components';
import {
  attachmentPresentation,
  grantPendingPresentation,
  networkErrorInfo,
  useManageNetworks,
  useNetworking,
  waitingWords,
} from '../shared';
import { attachmentRun, nameResolutionOff } from './helpers';
import { PlanNetworkButton } from './NetworkProblem';
import { ReleaseAddressesButton } from './ReleaseAddresses';

const s = strings.networks.deployment;
const n = strings.networks;

type Attachment = DeploymentNetwork['attachments'][number];

function RetryButton({ networkId, deploymentId }: { networkId: string; deploymentId: string }) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  return (
    <ActionButton
      permission="operate"
      className="btn-sm"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await api.connectAttachment(networkId, deploymentId);
          toast.show({ tone: 'success', title: s.retrySent });
          void queryClient.invalidateQueries({ queryKey: ['deployment', deploymentId] });
          void queryClient.invalidateQueries({ queryKey: ['runs'] });
        } catch (error) {
          toast.error(error);
        } finally {
          setBusy(false);
        }
      }}
    >
      {s.retry}
    </ActionButton>
  );
}

function AttachmentState({
  deployment,
  network,
  attachment,
  runs,
  onApply,
}: {
  deployment: Deployment;
  network: DeploymentNetwork;
  attachment: Attachment;
  runs: readonly Run[];
  onApply: () => void;
}) {
  const admin = useManageNetworks();
  const dev = deployment.name;
  const connect = attachmentRun(runs, deployment.id, 'connect');
  const detach = attachmentRun(runs, deployment.id, 'detach');
  const runLink = (run: Run | undefined, label: string) =>
    run ? (
      <Link className="link" to={`/runs/${run.id}`}>
        {label}
      </Link>
    ) : null;
  const words = (presentation: Parameters<typeof StatusBadge>[0]['presentation'], extra?: ReactNode) => (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <StatusBadge presentation={presentation} />
      {extra}
    </span>
  );

  if (network.mode === 'retained') return <span>{s.ownNetwork}</span>;
  const grantPending =
    attachment.lastError?.code === 'NETWORK_GRANT_PENDING' && attachment.waitingFor?.reason === 'not-before';
  if (grantPending)
    return words(
      grantPendingPresentation(waitingWords(attachment.waitingFor)),
      runLink(connect, s.connectRun),
    );
  switch (attachment.state) {
    case 'connected':
      return words(
        { label: s.connected, tone: 'success', icon: CircleCheck },
        attachment.connectedAt && (
          <span className="text-fg-muted" title={formatDateTime(attachment.connectedAt)}>
            {s.since(formatRelative(attachment.connectedAt))}
          </span>
        ),
      );
    case 'connecting':
      return words(
        attachmentPresentation('connecting'),
        <>
          {runLink(connect, s.connectRun)}
          {attachment.waitingFor && (
            <WaitingText waitingFor={attachment.waitingFor} className="text-fg-muted" />
          )}
        </>,
      );
    case 'spoke-ready':
      return words(
        attachmentPresentation('spoke-ready'),
        <>
          {runLink(connect, s.connectRun)}
          {attachment.waitingFor && (
            <WaitingText waitingFor={attachment.waitingFor} className="text-fg-muted" />
          )}
        </>,
      );
    case 'pending':
      return words(
        attachmentPresentation('pending'),
        <>
          <span>{s.applyToConnect(dev)}</span>
          <ActionButton permission="operate" className="btn-sm" onClick={onApply}>
            {s.apply}
          </ActionButton>
        </>,
      );
    case 'detaching':
      return words(attachmentPresentation('detaching'), runLink(detach, s.detachRun));
    case 'failed': {
      const code = attachment.lastError?.code ?? '';
      const info = networkErrorInfo(code, { network: network.name, deployment: dev });
      if (info?.needsNetworkApply)
        return words(
          attachmentPresentation('failed'),
          admin ? (
            <>
              <span>{info.sentence}</span>
              <PlanNetworkButton networkId={network.id} />
            </>
          ) : (
            <span>{s.askAdmin(network.name)}</span>
          ),
        );
      return words(
        attachmentPresentation('failed'),
        <>
          <span>{info?.sentence ?? attachment.lastError?.message}</span>
          <RetryButton networkId={network.id} deploymentId={deployment.id} />
        </>,
      );
    }
    default:
      return words(attachmentPresentation(attachment.state));
  }
}

/** The Overview's first card while the deployment has a network; the standalone line while networking is on. */
export function NetworkCard({
  deployment,
  runs,
  onApply,
  onOpenSettings,
  onReleased,
}: {
  deployment: Deployment;
  /** The deployment's recent runs, newest first (with its connect and detach runs). */
  runs: readonly Run[];
  onApply: () => void;
  onOpenSettings: () => void;
  onReleased?: (deployment: Versioned<Deployment>) => void;
}) {
  const networking = useNetworking();
  const operate = usePermission('operate');
  const network = deployment.network ?? null;
  const loaded = useNetwork(network?.id ?? '', { enabled: networking.enabled && !!network });
  const dev = deployment.name;

  if (!network) {
    if (!networking.enabled) return null;
    return (
      <p className="network-standalone" data-testid="network-standalone">
        <NetworkIcon aria-hidden className="size-4 shrink-0 text-fg-muted" />
        <span>{s.standaloneLine(dev)}</span>
        {operate && (
          <button type="button" className="link" onClick={onOpenSettings}>
            {s.attachInSettings}
          </button>
        )}
      </p>
    );
  }

  const destroyed = loaded.data?.data.status === 'destroyed';
  return (
    <section aria-labelledby="network-card-heading" className="network-card" data-testid="network-card">
      <div className="network-card-head">
        <NetworkIcon aria-hidden className="size-4 shrink-0 text-fg-muted" />
        <h2 id="network-card-heading" className="text-[15px] font-semibold">
          {s.card}
        </h2>
        {networking.enabled ? (
          <Link className="link font-semibold" to={`/networks/${network.id}`}>
            {network.name}
          </Link>
        ) : (
          <span className="font-semibold">{network.name}</span>
        )}
        <span className="text-sm text-fg-muted">
          {s.topology[network.topology]} · {s.mode[network.mode]}
        </span>
      </div>
      {!networking.enabled ? (
        <p className="mt-2 text-sm" data-testid="network-card-off">
          {s.offReadOnly(dev)}
        </p>
      ) : (
        <>
          {destroyed && (
            <p className="callout callout-warning mt-2" data-testid="network-card-destroyed">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              <span className="min-w-0 flex-1">{n.errors.destroyed(network.name, dev)}</span>
              <button type="button" className="btn btn-sm" onClick={onOpenSettings}>
                {s.detach}
              </button>
            </p>
          )}
          {nameResolutionOff(runs) && (
            <p className="callout callout-warning mt-2" data-testid="network-card-dns-off">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              <span className="min-w-0 flex-1">{s.nameResolutionOff(dev)}</span>
              <ActionButton permission="operate" className="btn-sm" onClick={onApply}>
                {s.apply}
              </ActionButton>
            </p>
          )}
          <ul className="network-card-rows">
            {network.attachments.map((a) => (
              <li key={`${a.provider}/${a.region}`} data-testid={`network-row-${a.provider}-${a.region}`}>
                <span className="network-card-where">{s.cardRegion(a.provider, a.region)}</span>
                <span className="font-mono text-[12px]">{s.blockAndSize(a.cidr, a.size)}</span>
                <AttachmentState
                  deployment={deployment}
                  network={network}
                  attachment={a}
                  runs={runs}
                  onApply={onApply}
                />
              </li>
            ))}
          </ul>
          <NetworkCardRequests deployment={deployment} />
          <EntryRoutes deployment={deployment} origins={network.origins} onApply={onApply} />
          {network.mode === 'retained' && (
            <div className="mt-2">
              <ReleaseAddressesButton
                deployment={deployment}
                disabledReason={deployment.activeRunId ? s.runActive : null}
                onReleased={onReleased}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** The connect run's state in the interruption banner. */
export function ConnectRunState({ run }: { run: Run | null }) {
  if (!run) return null;
  if (run.status === 'pending')
    return run.waitingFor ? (
      <WaitingText waitingFor={run.waitingFor} />
    ) : (
      <span>
        <Clock aria-hidden className="mr-1 inline size-3.5" />
        {s.connectRunQueued}
      </span>
    );
  if (run.status === 'running') return <span>{s.connectRunRunning}</span>;
  if (run.status === 'failed') return <span>{s.connectRunFailed}</span>;
  return null;
}
